import { Track, type LocalParticipant } from 'livekit-client';

export type PublishedCameraDevice = {
  deviceId: string | null;
  label: string | null;
  trackId: string | null;
  width: number | null;
  height: number | null;
  frameRate: number | null;
  isPublishing: boolean;
};

type TrackWithMediaStream = {
  mediaStreamTrack?: MediaStreamTrack;
};

function getCameraTrack(localParticipant?: LocalParticipant | null) {
  const publication = localParticipant?.getTrackPublication(Track.Source.Camera);
  return {
    publication,
    track: publication?.track as TrackWithMediaStream | undefined,
  };
}

function getNumberSetting(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function getPublishedCameraDevice(localParticipant?: LocalParticipant | null): PublishedCameraDevice {
  const { publication, track } = getCameraTrack(localParticipant);
  const mediaStreamTrack = track?.mediaStreamTrack;
  const settings = mediaStreamTrack?.getSettings();
  const isPublishing = Boolean(mediaStreamTrack && publication?.isMuted !== true && mediaStreamTrack.readyState === 'live');

  return {
    deviceId: typeof settings?.deviceId === 'string' ? settings.deviceId : null,
    label: mediaStreamTrack?.label || null,
    trackId: mediaStreamTrack?.id || null,
    width: getNumberSetting(settings?.width),
    height: getNumberSetting(settings?.height),
    frameRate: getNumberSetting(settings?.frameRate),
    isPublishing,
  };
}

export function findMediaDevice(devices: MediaDeviceInfo[], deviceId: string | null | undefined) {
  if (!deviceId) return null;
  return devices.find(device => device.deviceId === deviceId) ?? null;
}

export function getMediaDeviceLabel(devices: MediaDeviceInfo[], deviceId: string | null | undefined) {
  return findMediaDevice(devices, deviceId)?.label || null;
}

export async function waitForPublishedCameraDevice(
  localParticipant: LocalParticipant | null | undefined,
  expectedDeviceId: string,
  timeoutMs = 1600
) {
  const startedAt = performance.now();
  let current = getPublishedCameraDevice(localParticipant);

  while (performance.now() - startedAt < timeoutMs) {
    current = getPublishedCameraDevice(localParticipant);
    if (current.deviceId === expectedDeviceId) break;
    await new Promise(resolve => window.setTimeout(resolve, 80));
  }

  return current;
}
